import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, Input } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';
import { ResponsiveTable } from '../../components/Table/ResponsiveTable.js';

interface CustomerRow {
  id: string;
  first_name: string;
  last_name: string;
  country: string | null;
  status: string;
  has_completed_kyc: boolean;
  created_at: string;
  email: string | null;
}

export function CustomersListPage() {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const customersQ = useQuery({
    queryKey: qk.customers({ search }),
    queryFn: () =>
      callFunction<{ items: CustomerRow[]; total: number }>('admin-users-list', {
        query: { search: search || undefined },
      }),
  });

  const items = customersQ.data?.items ?? [];

  const columns = [
    {
      key: 'name',
      label: t('customers.name'),
      render: (c: CustomerRow) => (
        <div className="flex flex-col">
          <Link to={`/customers/${c.id}`} className="text-primary hover:underline">
            {c.first_name} {c.last_name}
          </Link>
          {c.email ? <span className="text-xs text-text-muted">{c.email}</span> : null}
        </div>
      ),
    },
    {
      key: 'country',
      label: t('customers.country'),
      hideOnMobile: true,
      render: (c: CustomerRow) => <span>{c.country ?? '—'}</span>,
    },
    {
      key: 'kyc',
      label: t('customers.kyc'),
      render: (c: CustomerRow) => (
        <span>{c.has_completed_kyc ? t('common.yes') : t('common.no')}</span>
      ),
    },
    {
      key: 'status',
      label: t('customers.status'),
      render: (c: CustomerRow) => <span>{c.status}</span>,
    },
    {
      key: 'created',
      label: t('customers.created'),
      hideOnMobile: true,
      render: (c: CustomerRow) => (
        <span className="text-text-muted">
          {new Date(c.created_at).toLocaleDateString(
            i18n.language === 'fr' ? 'fr-FR' : 'en-US',
          )}
        </span>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('customers.title')}</CardTitle>
      </CardHeader>
      <div className="px-5 pb-3">
        <Input
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {customersQ.isLoading ? (
        <LoadingScreen />
      ) : customersQ.isError ? (
        <ErrorState onRetry={() => customersQ.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={t('customers.empty')} />
      ) : (
        <div className="px-5 pb-5">
          <ResponsiveTable
            columns={columns}
            data={items}
            keyExtractor={(c) => c.id}
            mobileLabel={(c) => `${c.first_name} ${c.last_name}`}
            emptyMessage={t('customers.empty')}
          />
        </div>
      )}
    </Card>
  );
}
